/**
 * textIcon.ts — слово владельца 21.10.2026 (задание stylepack/text-icon):
 * ЗНАЧОК ПЕРЕД СТРОКОЙ ТЕКСТА у заголовка, подзаголовка и абзаца, в
 * необязательной скруглённой плашке (Dance-bit: капля 21 px в голубой плашке
 * 32 px слева от «1 GLASS OF WATER = 8.45 FL OZ»).
 *
 * Границы, умолчания, перечень полей и типы — ЗЕРКАЛО сервера
 * (backend/src/ai-funnels/block-schema/text-icon.ts); разъезд ловит сторож
 * `text-icon.icon-ends.spec.ts` — он читает ЭТОТ файл с диска, форму
 * `{ min: N, max: N }` и списков не менять, не обновив его.
 *
 * Разметка (собирает PublicBlockRenderer): внутри САМОГО текстового тега —
 * строчный ряд `inline-flex` [ячейка значка][текст]. Тег сохраняет весь свой
 * стиль, поэтому выравнивание блока (`textAlign`, в том числе переменная
 * стиль-пака) двигает пару целиком, а вид текста (text-look) действует на
 * текст как прежде. Ячейка значка не ниже строки (`1lh` — высота строки
 * самого тега) и центрует значок: однострочный текст стоит по центру
 * значка, у многострочного значок встаёт по первой строке.
 *
 * Адрес — общий фильтр адресов (`safeHref`, та же дверь, что у ссылок и
 * крестика шапки): негодный или пустой — значка нет, разметка прежняя.
 */
import type { CSSProperties } from 'react';
import { safeHref } from '@/utils/isAllowedUrl';
import { clampLook } from './textLook';

/** Типы со значком перед текстом — зеркало `TEXT_ICON_BLOCK_TYPES` сервера. */
export const TEXT_ICON_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'title',
  'sub_title',
  'paragraph',
  // WEB-3919 (решение владельца 03.11)
  'heading',
]);

/** Поля значка — зеркало `textIconShape` сервера. */
export const TEXT_ICON_FIELDS = [
  'leadingIconUrl',
  'leadingIconSize',
  'leadingIconBoxSize',
  'leadingIconBoxColor',
  'leadingIconBoxRadius',
  'leadingIconGap',
] as const;

export const TEXT_ICON_BOUNDS = {
  leadingIconSize: { min: 12, max: 64 },
  leadingIconBoxSize: { min: 0, max: 96 },
  leadingIconBoxRadius: { min: 0, max: 48 },
  leadingIconGap: { min: 0, max: 32 },
} as const;

export const TEXT_ICON_DEFAULTS = {
  leadingIconSize: 20,
  leadingIconBoxRadius: 8,
  leadingIconGap: 8,
} as const;

/**
 * Код цвета блока — тот же, что `BLOCK_COLOR_PATTERN` поля цвета
 * (BlockColorField.tsx; совпадение держит textIcon.test.ts). Своя копия
 * только потому, что отрисовка покупателя не тянет компонент панели с antd.
 */
export const TEXT_ICON_COLOR_PATTERN =
  /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** Плашка без своего цвета — поверхность темы. */
const PLATE_FALLBACK = 'var(--color-bg-surface, #f2f2f2)';

export interface TextIconFields {
  leadingIconUrl?: string;
  leadingIconSize?: number;
  leadingIconBoxSize?: number;
  leadingIconBoxColor?: string;
  leadingIconBoxRadius?: number;
  leadingIconGap?: number;
}

export interface TextIconParts {
  src: string;
  row: CSSProperties;
  holder: CSSProperties;
  plate: CSSProperties | null;
  img: CSSProperties;
  text: CSSProperties;
}

/** Части разметки значка; `null` — значка нет (нет годного адреса). */
export function textIconParts(fields: TextIconFields): TextIconParts | null {
  const src = safeHref(fields.leadingIconUrl, '');
  if (!src) return null;
  const B = TEXT_ICON_BOUNDS;
  const D = TEXT_ICON_DEFAULTS;
  const size =
    clampLook(fields.leadingIconSize, B.leadingIconSize) ?? D.leadingIconSize;
  const box = clampLook(fields.leadingIconBoxSize, B.leadingIconBoxSize) ?? 0;
  const radius =
    clampLook(fields.leadingIconBoxRadius, B.leadingIconBoxRadius) ??
    D.leadingIconBoxRadius;
  const gap =
    clampLook(fields.leadingIconGap, B.leadingIconGap) ?? D.leadingIconGap;
  const color = fields.leadingIconBoxColor;
  const side = box > 0 ? box : size;
  return {
    src,
    row: {
      display: 'inline-flex',
      alignItems: 'flex-start',
      gap,
      maxWidth: '100%',
      verticalAlign: 'top',
    },
    holder: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '0 0 auto',
      width: side,
      height: `max(${side}px, 1lh)`,
    },
    plate:
      box > 0
        ? {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: box,
            height: box,
            borderRadius: radius,
            background:
              typeof color === 'string' && TEXT_ICON_COLOR_PATTERN.test(color)
                ? color
                : PLATE_FALLBACK,
          }
        : null,
    img: { display: 'block', width: size, height: size, objectFit: 'contain' },
    text: {
      minWidth: 0,
      paddingTop: `max(0px, calc((${side}px - 1lh) / 2))`,
    },
  };
}
